import { useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/index.js';
import { ClassroomsTab } from './ClassroomsTab.js';
import { SettingsTab } from './SettingsTab.js';
import styles from './HubShell.module.css';

type HubTab = 'classrooms' | 'settings';

function firstName(displayName: string | undefined | null): string {
  if (!displayName) return 'teacher';
  const trimmed = displayName.trim();
  if (!trimmed) return 'teacher';
  return trimmed.split(/\s+/)[0]!;
}

function timeOfDayGreeting(now: Date = new Date()): string {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

export interface HubShellProps {
  /** Drill into a classroom's detail/configuration screen. PhaseRouter
   *  owns the selected-id state so it survives Activate → live → back. */
  onSelectClassroom: (classroomId: string) => void;
}

/**
 * Hub root view. Greeting, tab strip (Classrooms / Settings), Logout.
 * Drilling into a classroom card yields the surface to ClassroomDetail
 * via PhaseRouter — which also owns the selected-id state so back
 * from live mode lands on the classroom's detail page rather than the
 * hub list.
 */
export function HubShell({ onSelectClassroom }: HubShellProps) {
  const auth = useAuth();
  const [tab, setTab] = useState<HubTab>('classrooms');

  return (
    <div className={styles.shell} data-testid="hub-shell">
      <header className={styles.header}>
        <h1 className={styles.greeting}>
          {timeOfDayGreeting()},{' '}
          <span className={styles.name}>{firstName(auth.account?.name)}</span>
        </h1>
        <p className={styles.subtitle}>Welcome to Classy.</p>
      </header>

      <nav className={`glass ${styles.tabs}`} role="tablist" aria-label="Hub sections">
        <TabButton tab="classrooms" current={tab} onClick={() => setTab('classrooms')}>
          Classrooms
        </TabButton>
        <TabButton tab="settings" current={tab} onClick={() => setTab('settings')}>
          Settings
        </TabButton>
        <button
          type="button"
          className={styles.logout}
          onClick={() => auth.signOut()}
          data-testid="hub-logout"
        >
          Logout
        </button>
      </nav>

      <section className={styles.body}>
        {tab === 'classrooms' ? <ClassroomsTab onSelectClassroom={onSelectClassroom} /> : null}
        {tab === 'settings' ? <SettingsTab /> : null}
      </section>
    </div>
  );
}

function TabButton({
  tab,
  current,
  onClick,
  children,
}: {
  tab: HubTab;
  current: HubTab;
  onClick: () => void;
  children: ReactNode;
}) {
  const selected = tab === current;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={selected}
      className={selected ? `${styles.tab} ${styles.tabActive}` : styles.tab}
      onClick={onClick}
      data-testid={`hub-tab-${tab}`}
    >
      {children}
    </button>
  );
}
